/**
 * Inventory rules (plan 3.8–3.12): FIFO / FEFO picking, reservations,
 * replenishment, stock counts, transit receipt. Merged into stockLocales.
 */
export const inventoryRulesLocales = {
  en: {
    nav: {
      reservations: "Reservations",
      replenishment: "Replenishment",
      stockCounts: "Stock Counts",
      inventoryPolicies: "Inventory Policies"
    },
    stock: {
      dispatchErrors: {
        LOT_SPLIT_INVALID: "The lot split for {{sku}} is invalid: use LOT:qty pairs that add up to the line quantity."
      },
      picking: {
        rule: "Picking rule",
        inherit: "Inherit from policy",
        rules: { fifo: "FIFO — first in, first out", fefo: "FEFO — first expiry, first out" },
        suggestedBy: "Suggested by {{rule}}",
        splitHint: "Several lots: LOT-A:3, LOT-B:2",
        shortfall: "{{qty}} not covered by usable lots"
      },
      reservations: {
        title: "Reservations",
        subtitle: "Stock held for sales and work orders, by priority and expiry.",
        expiredReleased: "{{count}} expired reservation(s) released.",
        loadError: "Reservations could not be loaded.",
        actionError: "The reservation could not be updated.",
        allStatuses: "All statuses",
        statuses: { active: "Active", consumed: "Consumed", released: "Released", expired: "Expired", preempted: "Taken over" },
        empty: "No reservations.",
        code: "Order",
        source: "Source",
        sources: { salesOrders: "Sales order", manufacturingOrders: "Work order" },
        priority: "Priority",
        priorities: { 1: "1 — Urgent", 2: "2 — High", 3: "3 — Normal", 4: "4 — Low" },
        defaultPriority: "Policy default",
        lines: "Lines",
        held: "Held",
        expires: "Expires",
        shortage: "Short",
        locked: "Locked (picking started)",
        hoursLeft: "{{count}} h left",
        saved: "Priority saved.",
        extended: "Reservation extended.",
        hours: "+24 h",
        release: "Release",
        releasedMsg: "Reservation released.",
        insufficient: "Not enough available stock for {{sku}}.",
        preemptConfirm: "Not enough available stock for {{sku}}. Release lower-priority reservations to cover this order?",
        moShortages: "Materials not fully reserved:"
      },
      replenishment: {
        title: "Replenishment",
        subtitle: "Reorder point, lead time and min / max: purchase and transfer proposals.",
        loadError: "The replenishment plan could not be calculated.",
        requestCreated: "Purchase request created for {{sku}}.",
        transferCreated: "Transfer created for {{sku}}; it is now in transit.",
        actionError: "The proposal could not be created.",
        usageWindow: "Usage window",
        days: "{{count}} days",
        onlyNeeded: "Only items to order",
        formula: "Reorder point = daily usage × lead time + safety stock. Order when available + in transit + on order ≤ reorder point, up to max (or reorder point + one lead time of usage), rounded up to the minimum order and purchase unit.",
        empty: "Nothing to replenish.",
        cols: {
          dailyUsage: "Daily usage",
          leadTime: "Lead time (d)",
          rop: "Reorder point",
          position: "Position",
          target: "Target",
          order: "Order",
          cover: "Cover (d)"
        },
        ropSource: { manual: "manual", computed: "calculated" },
        positionHint: "Available {{available}} + in transit {{inTransit}} + on order {{onOrder}}",
        onOrder: "On order",
        ok: "OK",
        requested: "Requested",
        createRequest: "Purchase request",
        transfers: "Transfer proposals between warehouses",
        noTransfers: "No warehouse is below its minimum while another has surplus.",
        createTransfer: "Create transfer",
        productTitle: "Replenishment and picking",
        autoRop: "Leave empty to calculate from usage and lead time.",
        warehouseTargets: "Warehouse min / max",
        purchaseUom: "Purchase unit",
        fields: {
          reorderPoint: "Reorder point",
          safetyStock: "Safety stock",
          leadTimeDays: "Lead time (days)",
          dailyUsage: "Daily usage (override)",
          minOrderQty: "Minimum order"
        },
        errors: {
          nonNegative: "Values cannot be negative.",
          integer: "Lead time must be whole days.",
          maxBelowMin: "Max cannot be below min.",
          failed: "Settings could not be saved."
        }
      },
      counts: {
        title: "Stock Counts",
        subtitle: "Full, periodic and cycle (ABC) counts with variance approval.",
        loadError: "Counts could not be loaded.",
        actionError: "The count could not be updated.",
        errors: {
          COUNT_TYPE_INVALID: "Choose a count type.",
          WAREHOUSE_REQUIRED: "Choose a warehouse.",
          COUNT_SCOPE_REQUIRED: "A periodic count needs a product group or a location.",
          NOTHING_TO_COUNT: "No products match this count.",
          COUNT_NOT_OPEN: "This count is no longer open for entry.",
          COUNT_QTY_INVALID: "Counted quantities must be zero or more.",
          COUNT_INCOMPLETE: "Enter a quantity for every line before submitting.",
          COUNT_NOT_IN_REVIEW: "This count is not waiting for approval.",
          COUNT_POSTED: "A posted count cannot be cancelled."
        },
        created: "Count list created.",
        saved: "Counts saved.",
        submitted: "Count submitted for approval.",
        list: "Counts",
        new: "New count",
        empty: "No counts yet.",
        types: { full: "Full", periodic: "Periodic", cycle: "Cycle (ABC)" },
        typeHints: {
          full: "Every product in the warehouse.",
          periodic: "A product group or a location.",
          cycle: "Products whose ABC class is due for counting."
        },
        statuses: { counting: "Counting", review: "Awaiting approval", posting: "Posting", posted: "Posted", cancelled: "Cancelled" },
        blind: "Blind count",
        progress: "{{counted}} / {{total}} counted",
        varianceSummary: "{{lines}} line(s) with variance, net {{net}}",
        system: "System",
        counted: "Counted",
        diff: "Variance",
        recountBadge: "Recount {{n}}",
        booked: "Adjusted",
        cancel: "Cancel count",
        cancelled: "Count cancelled.",
        submit: "Submit",
        recount: "Send {{count}} line(s) to recount",
        recountSent: "Lines sent back for recount.",
        approve: "Approve and adjust ({{count}})",
        approved: "Variances approved and adjustment movements posted.",
        retryPosting: "Retry posting",
        pick: "Select",
        type: "Count type",
        wholeWarehouse: "Whole warehouse",
        productGroup: "Product group",
        allGroups: "All groups",
        classes: "ABC classes",
        name: "Count name",
        blindHint: "System quantities stay hidden until the count is submitted.",
        start: "Create count list"
      },
      transit: {
        days: "Days",
        receivedQty: "Received quantity",
        sent: "Sent",
        shortageNote: "Shortage note",
        shortageHint: "Required when less than the sent quantity arrives; the difference is written off as transit loss.",
        receivedShort: "Transfer received; {{qty}} recorded as transit loss.",
        errors: {
          RECEIVED_QTY_INVALID: "The received quantity must be between 0 and the sent quantity.",
          SHORTAGE_NOTE_REQUIRED: "Explain the shortage before receiving."
        }
      },
      policies: {
        title: "Inventory Policies",
        subtitle: "Picking rule by product group, reservation expiry and cycle count frequency.",
        picking: "FIFO / FEFO by product group",
        defaultPicking: "Default rule",
        autoPicking: "Automatic (FEFO for expiry-tracked items, otherwise FIFO)",
        pickingHint: "A rule set on the product overrides its group.",
        reservations: "Reservations",
        salesHours: "Sales order hold (h)",
        productionHours: "Work order hold (h)",
        defaultPriority: "Default priority",
        reservationHint: "0 h keeps the reservation until it is released. Picking locks a reservation so it cannot expire.",
        cycleCount: "Cycle count (ABC)",
        aShare: "Class A: cumulative usage share %",
        bShare: "Class B: cumulative usage share %",
        aDays: "Count A every (days)",
        bDays: "Count B every (days)",
        cDays: "Count C every (days)",
        abcHint: "Products are ranked by usage value over the last 90 days.",
        saved: "Policies saved.",
        errors: {
          abcShares: "Shares must satisfy 0 < A < B ≤ 100.",
          failed: "Policies could not be saved."
        }
      }
    }
  },
  tr: {
    nav: {
      reservations: "Rezervasyonlar",
      replenishment: "İkmal",
      stockCounts: "Stok Sayımı",
      inventoryPolicies: "Stok Politikaları"
    },
    stock: {
      dispatchErrors: {
        LOT_SPLIT_INVALID: "{{sku}} için lot dağılımı geçersiz: satır miktarına eşit toplamda LOT:miktar çiftleri girin."
      },
      picking: {
        rule: "Çıkış kuralı",
        inherit: "Politikadan al",
        rules: { fifo: "FIFO — ilk giren ilk çıkar", fefo: "FEFO — ilk tükenecek ilk çıkar" },
        suggestedBy: "{{rule}} önerisi",
        splitHint: "Birden çok lot: LOT-A:3, LOT-B:2",
        shortfall: "{{qty}} adet kullanılabilir lotla karşılanamıyor"
      },
      reservations: {
        title: "Rezervasyonlar",
        subtitle: "Satış siparişi ve iş emri için ayrılan stok; öncelik ve süre sonu.",
        expiredReleased: "Süresi dolan {{count}} rezervasyon serbest bırakıldı.",
        loadError: "Rezervasyonlar yüklenemedi.",
        actionError: "Rezervasyon güncellenemedi.",
        allStatuses: "Tüm durumlar",
        statuses: { active: "Aktif", consumed: "Tüketildi", released: "Serbest", expired: "Süresi doldu", preempted: "Öne geçildi" },
        empty: "Rezervasyon yok.",
        code: "Sipariş",
        source: "Kaynak",
        sources: { salesOrders: "Satış siparişi", manufacturingOrders: "İş emri" },
        priority: "Öncelik",
        priorities: { 1: "1 — Acil", 2: "2 — Yüksek", 3: "3 — Normal", 4: "4 — Düşük" },
        defaultPriority: "Politika varsayılanı",
        lines: "Satırlar",
        held: "Ayrılan",
        expires: "Bitiş",
        shortage: "Eksik",
        locked: "Kilitli (toplama başladı)",
        hoursLeft: "{{count}} sa kaldı",
        saved: "Öncelik kaydedildi.",
        extended: "Rezervasyon uzatıldı.",
        hours: "+24 sa",
        release: "Serbest bırak",
        releasedMsg: "Rezervasyon serbest bırakıldı.",
        insufficient: "{{sku}} için yeterli kullanılabilir stok yok.",
        preemptConfirm: "{{sku}} için yeterli kullanılabilir stok yok. Bu siparişi karşılamak için daha düşük öncelikli rezervasyonlar serbest bırakılsın mı?",
        moShortages: "Tam rezerve edilemeyen malzemeler:"
      },
      replenishment: {
        title: "İkmal",
        subtitle: "Yeniden sipariş noktası, tedarik süresi ve min / max: satın alma ve transfer önerileri.",
        loadError: "İkmal planı hesaplanamadı.",
        requestCreated: "{{sku}} için satın alma talebi oluşturuldu.",
        transferCreated: "{{sku}} için transfer oluşturuldu; yolda durumunda.",
        actionError: "Öneri oluşturulamadı.",
        usageWindow: "Tüketim dönemi",
        days: "{{count}} gün",
        onlyNeeded: "Yalnızca sipariş gerekenler",
        formula: "Yeniden sipariş noktası = günlük tüketim × tedarik süresi + emniyet stoku. Kullanılabilir + yolda + siparişte ≤ yeniden sipariş noktası olduğunda, max'a (yoksa yeniden sipariş noktası + bir tedarik süresi tüketimine) kadar sipariş verilir; asgari sipariş ve satın alma birimine yukarı yuvarlanır.",
        empty: "İkmal gereken ürün yok.",
        cols: {
          dailyUsage: "Günlük tüketim",
          leadTime: "Tedarik (gün)",
          rop: "Yeniden sipariş noktası",
          position: "Durum",
          target: "Hedef",
          order: "Sipariş",
          cover: "Yeterlilik (gün)"
        },
        ropSource: { manual: "elle", computed: "hesaplanan" },
        positionHint: "Kullanılabilir {{available}} + yolda {{inTransit}} + siparişte {{onOrder}}",
        onOrder: "Siparişte",
        ok: "Yeterli",
        requested: "Talep edildi",
        createRequest: "Satın alma talebi",
        transfers: "Depolar arası transfer önerileri",
        noTransfers: "Fazlası olan bir depo varken minimumun altında kalan depo yok.",
        createTransfer: "Transfer oluştur",
        productTitle: "İkmal ve çıkış kuralı",
        autoRop: "Boş bırakılırsa tüketim ve tedarik süresinden hesaplanır.",
        warehouseTargets: "Depo bazında min / max",
        purchaseUom: "Satın alma birimi",
        fields: {
          reorderPoint: "Yeniden sipariş noktası",
          safetyStock: "Emniyet stoku",
          leadTimeDays: "Tedarik süresi (gün)",
          dailyUsage: "Günlük tüketim (elle)",
          minOrderQty: "Asgari sipariş"
        },
        errors: {
          nonNegative: "Değerler negatif olamaz.",
          integer: "Tedarik süresi tam gün olmalı.",
          maxBelowMin: "Max, min'den küçük olamaz.",
          failed: "Ayarlar kaydedilemedi."
        }
      },
      counts: {
        title: "Stok Sayımı",
        subtitle: "Tam, dönemsel ve döngüsel (ABC) sayım; fark onayı ve düzeltme hareketi.",
        loadError: "Sayımlar yüklenemedi.",
        actionError: "Sayım güncellenemedi.",
        errors: {
          COUNT_TYPE_INVALID: "Sayım türü seçin.",
          WAREHOUSE_REQUIRED: "Depo seçin.",
          COUNT_SCOPE_REQUIRED: "Dönemsel sayım için ürün grubu veya lokasyon seçin.",
          NOTHING_TO_COUNT: "Bu sayıma uyan ürün yok.",
          COUNT_NOT_OPEN: "Bu sayım artık girişe açık değil.",
          COUNT_QTY_INVALID: "Sayılan miktarlar sıfır veya daha büyük olmalı.",
          COUNT_INCOMPLETE: "Göndermeden önce her satıra miktar girin.",
          COUNT_NOT_IN_REVIEW: "Bu sayım onay beklemiyor.",
          COUNT_POSTED: "Kaydedilmiş sayım iptal edilemez."
        },
        created: "Sayım listesi oluşturuldu.",
        saved: "Sayımlar kaydedildi.",
        submitted: "Sayım onaya gönderildi.",
        list: "Sayımlar",
        new: "Yeni sayım",
        empty: "Henüz sayım yok.",
        types: { full: "Tam", periodic: "Dönemsel", cycle: "Döngüsel (ABC)" },
        typeHints: {
          full: "Depodaki tüm ürünler.",
          periodic: "Bir ürün grubu veya lokasyon.",
          cycle: "ABC sınıfına göre sayım zamanı gelen ürünler."
        },
        statuses: { counting: "Sayılıyor", review: "Onay bekliyor", posting: "Kaydediliyor", posted: "Kaydedildi", cancelled: "İptal" },
        blind: "Kör sayım",
        progress: "{{counted}} / {{total}} sayıldı",
        varianceSummary: "{{lines}} satırda fark, net {{net}}",
        system: "Sistem",
        counted: "Sayılan",
        diff: "Fark",
        recountBadge: "Tekrar sayım {{n}}",
        booked: "Düzeltildi",
        cancel: "Sayımı iptal et",
        cancelled: "Sayım iptal edildi.",
        submit: "Gönder",
        recount: "{{count}} satırı tekrar saydır",
        recountSent: "Satırlar tekrar sayıma gönderildi.",
        approve: "Onayla ve düzelt ({{count}})",
        approved: "Farklar onaylandı, düzeltme hareketleri kaydedildi.",
        retryPosting: "Kaydı yeniden dene",
        pick: "Seç",
        type: "Sayım türü",
        wholeWarehouse: "Tüm depo",
        productGroup: "Ürün grubu",
        allGroups: "Tüm gruplar",
        classes: "ABC sınıfları",
        name: "Sayım adı",
        blindHint: "Sistem miktarları sayım gönderilene kadar gizli kalır.",
        start: "Sayım listesi oluştur"
      },
      transit: {
        days: "Gün",
        receivedQty: "Kabul edilen miktar",
        sent: "Gönderilen",
        shortageNote: "Eksik açıklaması",
        shortageHint: "Gönderilenden az mal gelirse zorunludur; fark transit kaybı olarak düşülür.",
        receivedShort: "Transfer kabul edildi; {{qty}} adet transit kaybı olarak kaydedildi.",
        errors: {
          RECEIVED_QTY_INVALID: "Kabul miktarı 0 ile gönderilen miktar arasında olmalı.",
          SHORTAGE_NOTE_REQUIRED: "Kabulden önce eksiği açıklayın."
        }
      },
      policies: {
        title: "Stok Politikaları",
        subtitle: "Ürün grubuna göre çıkış kuralı, rezervasyon süresi ve döngüsel sayım sıklığı.",
        picking: "Ürün grubuna göre FIFO / FEFO",
        defaultPicking: "Varsayılan kural",
        autoPicking: "Otomatik (SKT takipli ürünlerde FEFO, diğerlerinde FIFO)",
        pickingHint: "Üründe tanımlı kural, grubun kuralından önce gelir.",
        reservations: "Rezervasyonlar",
        salesHours: "Satış siparişi tutma (sa)",
        productionHours: "İş emri tutma (sa)",
        defaultPriority: "Varsayılan öncelik",
        reservationHint: "0 sa, rezervasyonu serbest bırakılana kadar tutar. Toplama başlayınca rezervasyon kilitlenir ve süresi dolmaz.",
        cycleCount: "Döngüsel sayım (ABC)",
        aShare: "A sınıfı: kümülatif tüketim payı %",
        bShare: "B sınıfı: kümülatif tüketim payı %",
        aDays: "A sayım sıklığı (gün)",
        bDays: "B sayım sıklığı (gün)",
        cDays: "C sayım sıklığı (gün)",
        abcHint: "Ürünler son 90 günlük tüketim değerine göre sıralanır.",
        saved: "Politikalar kaydedildi.",
        errors: {
          abcShares: "Paylar 0 < A < B ≤ 100 olmalı.",
          failed: "Politikalar kaydedilemedi."
        }
      }
    }
  },
  ar: {
    nav: {
      reservations: "الحجوزات",
      replenishment: "إعادة التموين",
      stockCounts: "جرد المخزون",
      inventoryPolicies: "سياسات المخزون"
    },
    stock: {
      dispatchErrors: {
        LOT_SPLIT_INVALID: "توزيع الدفعات لـ {{sku}} غير صالح: أدخل أزواج الدفعة:الكمية بمجموع يساوي كمية السطر."
      },
      picking: {
        rule: "قاعدة الصرف",
        inherit: "من السياسة",
        rules: { fifo: "FIFO — الوارد أولاً يُصرف أولاً", fefo: "FEFO — الأقرب انتهاءً يُصرف أولاً" },
        suggestedBy: "مقترح حسب {{rule}}",
        splitHint: "عدة دفعات: LOT-A:3, LOT-B:2",
        shortfall: "{{qty}} غير مغطاة بدفعات صالحة"
      },
      reservations: {
        title: "الحجوزات",
        subtitle: "المخزون المحجوز لأوامر البيع وأوامر العمل حسب الأولوية وتاريخ الانتهاء.",
        expiredReleased: "تم تحرير {{count}} حجز منتهي.",
        loadError: "تعذر تحميل الحجوزات.",
        actionError: "تعذر تحديث الحجز.",
        allStatuses: "كل الحالات",
        statuses: { active: "نشط", consumed: "مستهلك", released: "محرر", expired: "منتهي", preempted: "تم تجاوزه" },
        empty: "لا توجد حجوزات.",
        code: "الأمر",
        source: "المصدر",
        sources: { salesOrders: "أمر بيع", manufacturingOrders: "أمر عمل" },
        priority: "الأولوية",
        priorities: { 1: "1 — عاجل", 2: "2 — مرتفع", 3: "3 — عادي", 4: "4 — منخفض" },
        defaultPriority: "افتراضي السياسة",
        lines: "الأسطر",
        held: "محجوز",
        expires: "ينتهي",
        shortage: "نقص",
        locked: "مقفل (بدأ التجميع)",
        hoursLeft: "متبقي {{count}} ساعة",
        saved: "تم حفظ الأولوية.",
        extended: "تم تمديد الحجز.",
        hours: "+24 ساعة",
        release: "تحرير",
        releasedMsg: "تم تحرير الحجز.",
        insufficient: "لا يوجد مخزون متاح كافٍ لـ {{sku}}.",
        preemptConfirm: "لا يوجد مخزون متاح كافٍ لـ {{sku}}. هل تريد تحرير الحجوزات الأقل أولوية لتغطية هذا الأمر؟",
        moShortages: "مواد غير محجوزة بالكامل:"
      },
      replenishment: {
        title: "إعادة التموين",
        subtitle: "نقطة إعادة الطلب ومدة التوريد والحد الأدنى / الأقصى: مقترحات شراء وتحويل.",
        loadError: "تعذر حساب خطة إعادة التموين.",
        requestCreated: "تم إنشاء طلب شراء لـ {{sku}}.",
        transferCreated: "تم إنشاء تحويل لـ {{sku}}؛ وهو الآن في الطريق.",
        actionError: "تعذر إنشاء المقترح.",
        usageWindow: "فترة الاستهلاك",
        days: "{{count}} يوم",
        onlyNeeded: "فقط ما يحتاج طلباً",
        formula: "نقطة إعادة الطلب = الاستهلاك اليومي × مدة التوريد + مخزون الأمان. يُطلب عندما يكون المتاح + في الطريق + قيد الطلب ≤ نقطة إعادة الطلب، حتى الحد الأقصى (أو نقطة إعادة الطلب + استهلاك مدة توريد واحدة)، مع التقريب للأعلى إلى الحد الأدنى للطلب ووحدة الشراء.",
        empty: "لا شيء يحتاج إعادة تموين.",
        cols: {
          dailyUsage: "الاستهلاك اليومي",
          leadTime: "مدة التوريد (يوم)",
          rop: "نقطة إعادة الطلب",
          position: "الوضع",
          target: "الهدف",
          order: "الطلب",
          cover: "التغطية (يوم)"
        },
        ropSource: { manual: "يدوي", computed: "محسوب" },
        positionHint: "متاح {{available}} + في الطريق {{inTransit}} + قيد الطلب {{onOrder}}",
        onOrder: "قيد الطلب",
        ok: "كافٍ",
        requested: "تم الطلب",
        createRequest: "طلب شراء",
        transfers: "مقترحات التحويل بين المستودعات",
        noTransfers: "لا يوجد مستودع تحت الحد الأدنى بينما لدى آخر فائض.",
        createTransfer: "إنشاء تحويل",
        productTitle: "إعادة التموين وقاعدة الصرف",
        autoRop: "اتركه فارغاً ليُحسب من الاستهلاك ومدة التوريد.",
        warehouseTargets: "الحد الأدنى / الأقصى لكل مستودع",
        purchaseUom: "وحدة الشراء",
        fields: {
          reorderPoint: "نقطة إعادة الطلب",
          safetyStock: "مخزون الأمان",
          leadTimeDays: "مدة التوريد (أيام)",
          dailyUsage: "الاستهلاك اليومي (يدوي)",
          minOrderQty: "الحد الأدنى للطلب"
        },
        errors: {
          nonNegative: "لا يمكن أن تكون القيم سالبة.",
          integer: "يجب أن تكون مدة التوريد أياماً كاملة.",
          maxBelowMin: "لا يمكن أن يكون الحد الأقصى أقل من الأدنى.",
          failed: "تعذر حفظ الإعدادات."
        }
      },
      counts: {
        title: "جرد المخزون",
        subtitle: "جرد كامل ودوري ودوري حسب ABC مع اعتماد الفروقات.",
        loadError: "تعذر تحميل عمليات الجرد.",
        actionError: "تعذر تحديث الجرد.",
        errors: {
          COUNT_TYPE_INVALID: "اختر نوع الجرد.",
          WAREHOUSE_REQUIRED: "اختر مستودعاً.",
          COUNT_SCOPE_REQUIRED: "يتطلب الجرد الدوري مجموعة منتجات أو موقعاً.",
          NOTHING_TO_COUNT: "لا توجد منتجات تطابق هذا الجرد.",
          COUNT_NOT_OPEN: "هذا الجرد لم يعد مفتوحاً للإدخال.",
          COUNT_QTY_INVALID: "يجب أن تكون الكميات المعدودة صفراً أو أكثر.",
          COUNT_INCOMPLETE: "أدخل كمية لكل سطر قبل الإرسال.",
          COUNT_NOT_IN_REVIEW: "هذا الجرد لا ينتظر الاعتماد.",
          COUNT_POSTED: "لا يمكن إلغاء جرد مُرحّل."
        },
        created: "تم إنشاء قائمة الجرد.",
        saved: "تم حفظ الكميات.",
        submitted: "تم إرسال الجرد للاعتماد.",
        list: "عمليات الجرد",
        new: "جرد جديد",
        empty: "لا توجد عمليات جرد بعد.",
        types: { full: "كامل", periodic: "دوري", cycle: "دوري (ABC)" },
        typeHints: {
          full: "كل المنتجات في المستودع.",
          periodic: "مجموعة منتجات أو موقع.",
          cycle: "المنتجات التي حان موعد جردها حسب فئة ABC."
        },
        statuses: { counting: "قيد العد", review: "بانتظار الاعتماد", posting: "قيد الترحيل", posted: "مُرحّل", cancelled: "ملغى" },
        blind: "جرد أعمى",
        progress: "تم عد {{counted}} / {{total}}",
        varianceSummary: "{{lines}} سطر بفرق، الصافي {{net}}",
        system: "النظام",
        counted: "المعدود",
        diff: "الفرق",
        recountBadge: "إعادة عد {{n}}",
        booked: "تمت التسوية",
        cancel: "إلغاء الجرد",
        cancelled: "تم إلغاء الجرد.",
        submit: "إرسال",
        recount: "إعادة عد {{count}} سطر",
        recountSent: "أُعيدت الأسطر لإعادة العد.",
        approve: "اعتماد وتسوية ({{count}})",
        approved: "تم اعتماد الفروقات وترحيل حركات التسوية.",
        retryPosting: "إعادة محاولة الترحيل",
        pick: "اختيار",
        type: "نوع الجرد",
        wholeWarehouse: "كل المستودع",
        productGroup: "مجموعة المنتجات",
        allGroups: "كل المجموعات",
        classes: "فئات ABC",
        name: "اسم الجرد",
        blindHint: "تبقى كميات النظام مخفية حتى إرسال الجرد.",
        start: "إنشاء قائمة الجرد"
      },
      transit: {
        days: "أيام",
        receivedQty: "الكمية المستلمة",
        sent: "المرسل",
        shortageNote: "ملاحظة النقص",
        shortageHint: "مطلوبة عند وصول كمية أقل من المرسلة؛ يُشطب الفرق كفقد أثناء النقل.",
        receivedShort: "تم استلام التحويل؛ سُجلت {{qty}} كفقد أثناء النقل.",
        errors: {
          RECEIVED_QTY_INVALID: "يجب أن تكون الكمية المستلمة بين 0 والكمية المرسلة.",
          SHORTAGE_NOTE_REQUIRED: "اشرح النقص قبل الاستلام."
        }
      },
      policies: {
        title: "سياسات المخزون",
        subtitle: "قاعدة الصرف حسب مجموعة المنتجات، ومدة الحجز، وتكرار الجرد الدوري.",
        picking: "FIFO / FEFO حسب مجموعة المنتجات",
        defaultPicking: "القاعدة الافتراضية",
        autoPicking: "تلقائي (FEFO للمنتجات ذات تاريخ الانتهاء، وإلا FIFO)",
        pickingHint: "القاعدة المحددة على المنتج تتقدم على مجموعته.",
        reservations: "الحجوزات",
        salesHours: "مدة حجز أمر البيع (ساعة)",
        productionHours: "مدة حجز أمر العمل (ساعة)",
        defaultPriority: "الأولوية الافتراضية",
        reservationHint: "0 ساعة يبقي الحجز حتى تحريره. بدء التجميع يقفل الحجز فلا ينتهي.",
        cycleCount: "الجرد الدوري (ABC)",
        aShare: "الفئة A: النسبة التراكمية للاستهلاك %",
        bShare: "الفئة B: النسبة التراكمية للاستهلاك %",
        aDays: "جرد A كل (أيام)",
        bDays: "جرد B كل (أيام)",
        cDays: "جرد C كل (أيام)",
        abcHint: "تُرتب المنتجات حسب قيمة الاستهلاك في آخر 90 يوماً.",
        saved: "تم حفظ السياسات.",
        errors: {
          abcShares: "يجب أن تحقق النسب 0 < A < B ≤ 100.",
          failed: "تعذر حفظ السياسات."
        }
      }
    }
  }
};
